/**
 * mSTEM localStorage helpers. Wraps window.localStorage access in try/catch
 * so private-browsing / iframe-sandbox / SSR environments fall back to
 * in-memory defaults without throwing. Mirrors the Phase 1 detect-locale.js
 * pattern so behavior stays consistent across the app.
 *
 * Keys are namespaced under "mstem.*" to avoid collisions with Scratch /
 * OpenBlock existing keys.
 *
 * Security note (T-02-01-01): readRobots / readKits shape-validate the parsed
 * JSON so a user-tampered localStorage entry cannot inject arbitrary objects
 * into the reducer. On any shape mismatch we return null; the caller then
 * falls back to the bundled mock arrays from src/lib/mstem-mock/.
 */

const THEME_KEY = 'mstem.theme';
const ROBOTS_KEY = 'mstem.robots';
const KITS_KEY = 'mstem.kits';
const MY_PROJECTS_KEY = 'mstem.myProjects';
const SEED_HASH_KEY = 'mstem.myProjects.seedHash';

const safeRead = function (key) {
    try {
        return window.localStorage.getItem(key);
    } catch (e) {
        return null;
    }
};

const safeWrite = function (key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch (e) {
        /* swallow — private browsing / quota / SSR */
    }
};

const readTheme = function () {
    return safeRead(THEME_KEY);
};

const writeTheme = function (themeId) {
    safeWrite(THEME_KEY, themeId);
};

const readJsonList = function (key) {
    const raw = safeRead(key);
    if (!raw) return null;
    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return null;
    }
    if (!Array.isArray(parsed)) return null;
    // Shape guard — every entry must at minimum have an id + name string.
    // Stops attacker-controlled JSON from injecting unknown fields into reducers.
    for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];
        if (!item || typeof item.id !== 'string' || typeof item.name !== 'string') {
            return null;
        }
    }
    return parsed;
};

const writeJsonList = function (key, list) {
    try {
        safeWrite(key, JSON.stringify(list));
    } catch (e) {
        /* swallow — circular reference */
    }
};

const readRobots = function () {
    return readJsonList(ROBOTS_KEY);
};

const writeRobots = function (list) {
    writeJsonList(ROBOTS_KEY, list);
};

const readKits = function () {
    return readJsonList(KITS_KEY);
};

const writeKits = function (list) {
    writeJsonList(KITS_KEY, list);
};

// Phase 3 / Plan 03-01 additions — myProjects + seed-hash helpers.
// Reuse the readJsonList shape guard (requires id + name strings) so a
// tampered mstem.myProjects entry cannot inject unknown fields into the
// reducer. Caller wraps these via src/lib/mstem-project-storage.js for the
// per-entry vmState size cap + onQuotaError callback.
const readMyProjects = function () {
    return readJsonList(MY_PROJECTS_KEY);
};

const writeMyProjects = function (list) {
    writeJsonList(MY_PROJECTS_KEY, list);
};

const readSeedHash = function () {
    return safeRead(SEED_HASH_KEY);
};

const writeSeedHash = function (hash) {
    safeWrite(SEED_HASH_KEY, hash);
};

export {
    readTheme,
    writeTheme,
    readRobots,
    writeRobots,
    readKits,
    writeKits,
    readMyProjects,
    writeMyProjects,
    readSeedHash,
    writeSeedHash,
    // Phase 6 / Plan 06-01 — expose the generic guard helpers so
    // mstem-extension-storage.js reuses the SAME shape-guard + try/catch
    // safeRead/safeWrite (T-06-01-01) instead of duplicating them.
    readJsonList,
    writeJsonList,
    safeRead,
    safeWrite,
    THEME_KEY,
    ROBOTS_KEY,
    KITS_KEY,
    MY_PROJECTS_KEY,
    SEED_HASH_KEY
};
